/**
 * Chat Service
 *
 * Main orchestration service for AI chat functionality
 * Coordinates between frontend UI and backend Agent Workflow
 *
 * Responsibilities:
 * - Input sanitization and security checks
 * - Rate limiting
 * - Backend API communication
 * - Response validation
 * - Session management
 * - Analytics logging
 *
 * @module chatService
 * @version 1.0.0
 * @created 2025-11-12
 * @author Newso Technologies Consultants Co., Ltd.
 */

import { Message, ChatResponse, StreamingStatus, StreamingCallback } from '../types/chat.types';
import { SecurityService } from './securityService';
import { backendApiService } from './backendApiService';

export class ChatService {
  private securityService: SecurityService;
  private sessionId: string;

  constructor() {
    this.securityService = new SecurityService();
    this.sessionId = this.generateSessionId();
  }

  /**
   * Send a message and get AI response
   *
   * Complete message processing pipeline:
   * 1. Security checks (rate limit, sanitization, prompt injection)
   * 2. Call backend API (which runs Agents SDK workflow)
   * 3. Validate response
   * 4. Mask sensitive data
   * 5. Log interaction
   *
   * @param query - User query text
   * @param history - Conversation history (unused in current implementation)
   * @returns Chat response with answer and metadata
   */
  async sendMessage(query: string, history: Message[] = []): Promise<ChatResponse> {
    try {
      // Security checks
      if (!this.securityService.checkRateLimit(this.sessionId)) {
        throw new Error('คุณส่งคำถามบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่อีกครั้ง');
      }

      // Sanitize input
      const sanitizedQuery = this.securityService.sanitizeInput(query);

      // Check for prompt injection
      if (!this.securityService.checkPromptInjection(sanitizedQuery)) {
        this.securityService.logSecurityEvent({
          type: 'prompt_injection',
          severity: 'high',
          details: `Potential prompt injection: ${sanitizedQuery.substring(0, 100)}`,
          userId: this.sessionId,
        });

        throw new Error('ระบบตรวจพบคำถามที่ไม่เหมาะสม กรุณาปรับคำถามและลองใหม่อีกครั้ง');
      }

      // Check for offensive content
      if (!this.securityService.checkOffensiveContent(sanitizedQuery)) {
        this.securityService.logSecurityEvent({
          type: 'offensive_content',
          severity: 'medium',
          details: `Offensive content detected: ${sanitizedQuery.substring(0, 100)}`,
          userId: this.sessionId,
        });

        throw new Error('กรุณาใช้คำถามที่เหมาะสมในการสนทนา');
      }

      /**
       * Call backend API to execute workflow
       * Backend runs:
       * - @openai/guardrails (PII, Moderation, Jailbreak)
       * - @openai/agents (Agent execution)
       * - Vector Store search (reads PDF content)
       * - withTrace() (sends traces to OpenAI platform)
       */
      const workflowResult = await backendApiService.analyze(sanitizedQuery);

      /**
       * Convert backend workflow result to chat response format
       * Maps workflow output to chat UI requirements
       * Includes images if available from PDF
       */
      const response: ChatResponse = {
        answer: workflowResult.output_text || 'ขออภัยครับ ไม่สามารถตอบคำถามนี้ได้ในขณะนี้',
        sources: [],
        confidence: workflowResult.confidence || 0.8,
        workflow_execution_id: `wf_${Date.now()}`,
        has_images: workflowResult.has_images,
        images: workflowResult.images,
        images_processed: workflowResult.images_processed,
        cost_estimate: workflowResult.cost_estimate,
        processing_time: workflowResult.processing_time,
      };

      // Validate response
      const validation = this.securityService.validateResponse(response.answer);

      if (!validation.isValid) {
        console.error('Response validation failed:', validation.reason);

        this.securityService.logSecurityEvent({
          type: 'validation_error',
          severity: 'medium',
          details: `Response validation failed: ${validation.reason}`,
          userId: this.sessionId,
        });

        throw new Error('ขออภัยครับ ไม่สามารถตอบคำถามนี้ได้ในขณะนี้ กรุณาลองใหม่อีกครั้งครับ');
      }

      // Mask any sensitive data in response
      response.answer = this.securityService.maskSensitiveData(response.answer);

      // Log successful interaction
      this.logInteraction(query, response);

      return response;
    } catch (error: any) {
      console.error('❌ [ChatService] Error occurred:', error.message);

      // Return user-friendly error message
      throw new Error(error.message || 'เกิดข้อผิดพลาดในการประมวลผลคำถาม กรุณาลองใหม่อีกครั้ง');
    }
  }

  /**
   * Send a message with streaming response (SSE)
   *
   * Connects to backend streaming endpoint and processes Server-Sent Events
   * Calls callback function for each status update, text chunk, image, or completion
   *
   * @param query - User query text
   * @param callback - Callback function to handle streaming updates
   * @param maxPages - Maximum number of PDF pages to process (default: 5)
   * @returns Promise that resolves when streaming completes
   */
  async sendMessageStream(
    query: string,
    callback: StreamingCallback,
    maxPages: number = 5
  ): Promise<void> {
    try {
      // Security checks
      if (!this.securityService.checkRateLimit(this.sessionId)) {
        callback({
          type: 'error',
          message: 'คุณส่งคำถามบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่อีกครั้ง',
        });
        return;
      }

      // Sanitize input
      const sanitizedQuery = this.securityService.sanitizeInput(query);

      // Check for prompt injection
      if (!this.securityService.checkPromptInjection(sanitizedQuery)) {
        this.securityService.logSecurityEvent({
          type: 'prompt_injection',
          severity: 'high',
          details: `Potential prompt injection: ${sanitizedQuery.substring(0, 100)}`,
          userId: this.sessionId,
        });

        callback({
          type: 'error',
          message: 'ระบบตรวจพบคำถามที่ไม่เหมาะสม กรุณาปรับคำถามและลองใหม่อีกครั้ง',
        });
        return;
      }

      // Check for offensive content
      if (!this.securityService.checkOffensiveContent(sanitizedQuery)) {
        this.securityService.logSecurityEvent({
          type: 'offensive_content',
          severity: 'medium',
          details: `Offensive content detected: ${sanitizedQuery.substring(0, 100)}`,
          userId: this.sessionId,
        });

        callback({
          type: 'error',
          message: 'กรุณาใช้คำถามที่เหมาะสมในการสนทนา',
        });
        return;
      }

      const BACKEND_AI_URL = process.env.REACT_APP_BACKEND_AI_URL || 'https://localhost:3001/api';
      const streamEndpoint = `${BACKEND_AI_URL}/assistant/analyze/stream`;

      const response = await fetch(streamEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: sanitizedQuery,
          maxPages,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ [ChatService] HTTP ${response.status}:`, errorData.message);
        callback({
          type: 'error',
          message: errorData.message || `Backend API error: ${response.status}`,
        });
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        callback({
          type: 'error',
          message: 'ไม่สามารถเชื่อมต่อกับ streaming endpoint ได้',
        });
        return;
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data: StreamingStatus = JSON.parse(line.slice(6));
              callback(data);

              // If complete, validate and log
              if (data.type === 'complete' && data.result) {
                const validation = this.securityService.validateResponse(data.result.output_text);
                if (!validation.isValid) {
                  console.error('Response validation failed:', validation.reason);
                  callback({
                    type: 'error',
                    message: 'ขออภัยครับ ไม่สามารถตอบคำถามนี้ได้ในขณะนี้ กรุณาลองใหม่อีกครั้งครับ',
                  });
                  return;
                }

                // Log successful interaction
                const chatResponse: ChatResponse = {
                  answer: data.result.output_text,
                  sources: (data.result.sources || []).map((s: any) => ({
                    chunk_text: s.heading,
                    page_number: 0,
                    document_id: s.filename,
                    similarity_score: s.score,
                  })),
                  confidence: 0.8,
                  workflow_execution_id: `wf_${Date.now()}`,
                  has_images: data.result.has_images,
                  images: data.result.images,
                  images_processed: data.result.images_processed,
                  cost_estimate: data.result.cost_estimate,
                  processing_time: data.result.processing_time,
                };

                this.logInteraction(sanitizedQuery, chatResponse);
              }
            } catch (e) {
              console.error('❌ [ChatService] Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error: any) {
      console.error('❌ [ChatService] Streaming error:', error.message);
      console.error('❌ [ChatService] Error name:', error.name);
      console.error('❌ [ChatService] Error stack:', error.stack);
      console.error(
        '❌ [ChatService] Backend URL attempted:',
        `${
          process.env.REACT_APP_BACKEND_AI_URL || 'https://dev.fromas.cloud/v1/pms/ai'
        }/assistant/analyze/stream`
      );
      console.error('❌ [ChatService] Frontend origin:', window.location.origin);

      const BACKEND_AI_URL = process.env.REACT_APP_BACKEND_AI_URL || 'https://localhost:3001/api';
      const isProduction =
        !BACKEND_AI_URL.includes('localhost') && !BACKEND_AI_URL.includes('127.0.0.1');

      if (
        error.message.includes('fetch') ||
        error.message.includes('NetworkError') ||
        error.message.includes('Failed to fetch') ||
        error.name === 'TypeError'
      ) {
        if (isProduction) {
          callback({
            type: 'error',
            message:
              `ไม่สามารถเชื่อมต่อ Backend API ได้\n\n` +
              `Frontend: ${window.location.origin}\n` +
              `Backend: ${BACKEND_AI_URL}/assistant/analyze/stream\n\n` +
              `สาเหตุที่เป็นไปได้:\n` +
              `1. ปัญหา CORS (Cross-Origin Resource Sharing) - Backend ต้องอนุญาต origin: ${window.location.origin}\n` +
              `2. Backend server ไม่สามารถเข้าถึงได้\n` +
              `3. ปัญหา Network หรือ Firewall\n` +
              `4. Environment variable REACT_APP_BACKEND_AI_URL ไม่ได้ถูกตั้งค่า\n\n` +
              `กรุณาติดต่อผู้ดูแลระบบเพื่อตรวจสอบ CORS configuration ใน Backend`,
          });
        } else {
          callback({
            type: 'error',
            message:
              `ไม่สามารถเชื่อมต่อ Backend API ได้\n\n` +
              `Backend URL: ${BACKEND_AI_URL}/assistant/analyze/stream\n\n` +
              `กรุณาตรวจสอบว่า:\n` +
              `1. Backend server กำลังรันอยู่ (port 3002)\n` +
              `2. Streaming endpoint พร้อมใช้งาน\n` +
              `3. ตรวจสอบว่า REACT_APP_BACKEND_AI_URL ถูกตั้งค่าถูกต้อง`,
          });
        }
      } else {
        callback({
          type: 'error',
          message: error.message || 'เกิดข้อผิดพลาดในการประมวลผลคำถาม กรุณาลองใหม่อีกครั้ง',
        });
      }
    }
  }

  /**
   * Get chat history from local storage
   * @returns Array of previous messages
   */
  getChatHistory(): Message[] {
    try {
      const stored = localStorage.getItem(`fromas_chat_history_${this.sessionId}`);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load chat history:', error);
      return [];
    }
  }

  /**
   * Save chat history to local storage
   * @param messages - Array of messages to save
   */
  saveChatHistory(messages: Message[]): void {
    try {
      localStorage.setItem(`fromas_chat_history_${this.sessionId}`, JSON.stringify(messages));
    } catch (error) {
      console.error('Failed to save chat history:', error);
    }
  }

  /**
   * Clear chat history from storage
   */
  clearChatHistory(): void {
    try {
      localStorage.removeItem(`fromas_chat_history_${this.sessionId}`);
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
  }

  /**
   * Get current session ID
   * @returns Session identifier
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Generate unique session identifier
   * Format: session_{timestamp}_{random}
   * @returns Unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log interaction for analytics and debugging
   * Stores interaction data in localStorage
   * @param query - User query
   * @param response - AI response
   */
  private logInteraction(query: string, response: ChatResponse): void {
    const interaction = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      query,
      answer: response.answer,
      sources_count: response.sources?.length || 0,
      confidence: response.confidence || 0,
      workflow_id: response.workflow_execution_id,
    };

    try {
      const logs = JSON.parse(localStorage.getItem('fromas_chat_interactions') || '[]');
      logs.push(interaction);

      // Keep only last 500 interactions
      if (logs.length > 500) {
        logs.shift();
      }

      localStorage.setItem('fromas_chat_interactions', JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to log interaction:', error);
    }
  }

  /**
   * Get analytics data from logged interactions
   * @returns Analytics summary with interaction counts and confidence scores
   */
  getAnalytics(): {
    totalInteractions: number;
    averageConfidence: number;
    successRate: number;
  } {
    try {
      const logs = JSON.parse(localStorage.getItem('fromas_chat_interactions') || '[]');

      const totalInteractions = logs.length;
      const averageConfidence =
        logs.reduce((sum: number, log: any) => sum + (log.confidence || 0), 0) /
        (totalInteractions || 1);
      const successRate =
        logs.filter((log: any) => log.sources_count > 0).length / (totalInteractions || 1);

      return {
        totalInteractions,
        averageConfidence,
        successRate,
      };
    } catch (error) {
      console.error('Failed to get analytics:', error);
      return {
        totalInteractions: 0,
        averageConfidence: 0,
        successRate: 0,
      };
    }
  }
}
